import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { App as AntApp, ConfigProvider } from 'antd';
import enGB from 'antd/locale/en_GB';
import { useMemo, type ReactNode } from 'react';
import { PrintHost } from '../components/print/PrintHost';
import { buildTheme } from '../lib/antd-theme';
import { useThemeMode } from '../lib/theme-mode';
import { OFFLINE_MAX_AGE_MS, isPersistable, persister } from '../lib/offline-cache';
import '../lib/i18n';

/**
 * TanStack Query is the ONLY server-state cache (CLAUDE.md §10). No Redux, no
 * duplicated caches: two sources of truth for a customer record is exactly the
 * problem this product exists to solve.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A dealership's data changes while someone is looking at it, but not
      // second by second. 30s keeps a list responsive without hammering the API.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Must outlive the persisted cache, or a restored entry is garbage
      // collected before it can be shown (CLAUDE.md §14: offline-tolerant
      // reads). This is how long data may live in memory, not how long it is
      // trusted — staleTime still decides when to refetch.
      gcTime: OFFLINE_MAX_AGE_MS,
      // The api() helper already refreshes once on a 401; retrying a genuine
      // auth failure just delays the sign-in prompt.
      retry: (failureCount, error) =>
        failureCount < 2 && !(error instanceof Error && error.name === 'ApiError'),
    },
  },
});

/**
 * The cache survives a reload so an advisor with no signal still has their
 * pipeline. Only an allowlist of keys is written to disk — see offline-cache.ts
 * for what is admitted and, more importantly, what is not.
 */
export function Providers({ children }: { children: ReactNode }): ReactNode {
  // Built from the RESOLVED mode, not the choice: "system" is not a colour and
  // cannot be handed to a theme algorithm. Rebuilt when the mode changes so
  // AntD's tokens follow `data-theme` rather than lagging a render behind it.
  const { mode } = useThemeMode();
  const antdTheme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: OFFLINE_MAX_AGE_MS,
        dehydrateOptions: { shouldDehydrateQuery: isPersistable },
      }}
    >
      <ConfigProvider theme={antdTheme} locale={enGB}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
      {/* Outside ConfigProvider: paper never takes the app theme. */}
      <PrintHost />
    </PersistQueryClientProvider>
  );
}

export { queryClient };
